import React, { useState } from "react";
import InputField from "../generic/InputField";
import FormComponent from "../generic/FormComponent";
import AddAnotherItemButton from "../generic/AddAnotherItemButton";
import SubmitButton from "../generic/SubmitButton";
import AlertBanner from "../generic/AlertBanner";
import MaterialRow from "./MaterialRow"; // Updated MaterialRow Component
import { MESSAGE_TYPES, Messages, MessageType } from "../../constants/Messages";
import { createTemplate, Material } from "../../services/TemplateAPI";
import Modal from "../generic/Modal";
import { Template } from "../../types/Template";

interface CreateTemplateFormProps {
    onClose: () => void; // Function to close the modal
    initialValues?: Template; // Optional pre-filled data for duplication
}

const CreateTemplateForm: React.FC<CreateTemplateFormProps> = ({
                                                                   onClose, initialValues,
                                                               }) => {
    const defaultMaterialRow: Material = {
        material_type_id: null,
        material_size_id: null,
        quantity: 0,
        quantity_unit: "",
    };

    const [ templateName, setTemplateName ] = useState<string>(initialValues?.name || "");
    const [ materialRows, setMaterialRows ] = useState<Material[]>(
        initialValues?.materials || [ defaultMaterialRow ],
    );
    const [ isLoading, setIsLoading ] = useState<boolean>(false);
    const [ alert, setAlert ] = useState<{ message: string; type: MessageType } | null>(null);

    const handleRowChange = (index: number, field: keyof Material, value: any) => {
        const updatedRows = [ ...materialRows ];
        updatedRows[index][field] =
            field === "quantity" ? parseFloat(value.toString()) || 0 : value;
        setMaterialRows(updatedRows);
    };

    const addNewRow = () => {
        setMaterialRows((prevRows) => [ ...prevRows, { ...defaultMaterialRow } ]);
    };

    const removeRow = (index: number) => {
        setMaterialRows((prevRows) => prevRows.filter((_, i) => i !== index));
    };

    const handleSubmit = async () => {
        if (templateName.trim() === "") {
            setAlert({
                message: Messages.ERROR_TEMPLATE_NAME,
                type: MESSAGE_TYPES.ERROR,
            });
            return;
        }

        const validRows = materialRows
            .filter(
                (row) =>
                    row.material_type_id !== null &&
                    parseFloat(row.quantity.toString()) >= 0,
            )
            .map((row) => ({
                material_type_id: row.material_type_id,
                material_size_id: row.material_size_id,
                quantity: parseFloat(row.quantity.toString()), // Ensure numeric values
                quantity_unit: row.quantity_unit,
            }));

        if (validRows.length === 0) {
            setAlert({
                message: Messages.ERROR_NO_MATERIALS,
                type: MESSAGE_TYPES.ERROR,
            });
            return;
        }

        setIsLoading(true);

        try {
            const payload = { name: templateName, materials: validRows };
            const response = await createTemplate(payload); // Call API

            setAlert({
                message: response.message || Messages.SUCCESS_CREATE_TEMPLATE,
                type: MESSAGE_TYPES.SUCCESS,
            });

            // Reset form fields after successful submission
            setTemplateName("");
            setMaterialRows([ { ...defaultMaterialRow } ]);

            onClose(); // Close modal
        } catch (error: any) {
            setAlert({
                message: error.message || Messages.ERROR_CREATE_TEMPLATE,
                type: MESSAGE_TYPES.ERROR,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const closeAlert = () => setAlert(null);

    return (
        <Modal onClose={onClose}>
            <FormComponent title="Create Template" isModal={true}>
                {alert && (
                    <AlertBanner
                        message={alert.message}
                        message_type={alert.type}
                        onClose={closeAlert}
                    />
                )}

                <div className="mb-6">
                    <InputField
                        type="text"
                        value={templateName}
                        onChange={(value) => setTemplateName(value.toString())}
                        placeholder="Enter template name"
                        label="Template Name"
                        required
                    />
                </div>

                {materialRows.map((row, index) => (
                    <MaterialRow
                        key={index}
                        row={row}
                        index={index}
                        onChange={handleRowChange}
                        onRemove={removeRow}
                        showRemoveButton={materialRows.length > 1}
                    />
                ))}

                <div className="flex space-x-4">
                    <AddAnotherItemButton onAdd={addNewRow}/>
                    <SubmitButton
                        isLoading={isLoading}
                        disabled={templateName.trim() === "" || isLoading}
                        onClick={handleSubmit}
                    />
                </div>
            </FormComponent>
        </Modal>
    );
};

export default CreateTemplateForm;
